import React, { useState } from 'react';
import { AlertCircle, FilePlus, Loader2 } from 'lucide-react';
import { createSupplementQuote } from '../../services/quoteSupplementService';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';

interface Props {
    projectId?: string;
    /** Sprung ins Angebot (App: Angebotsliste mit diesem Angebot geöffnet). */
    onOpenQuote?: (quoteId: string) => void;
    isReadOnly?: boolean;
}

/**
 * „Nachtragsangebot anlegen" im Stammdaten-Bereich des Projekts.
 *
 * Legt einen leeren Entwurf zum Projekt an (Kunde, Typziffer und Texte vom
 * Hauptangebot, siehe createSupplementQuote) und springt in den Editor. Bei
 * der Annahme erhöhen seine Positionen die Vertragsmengen dieses Projekts —
 * es entsteht kein neues Projekt.
 *
 * Ob der Knopf überhaupt erscheint (Recht „Angebote"), entscheidet der
 * Aufrufer (zeigeNachtragKnopf); hier nur die Grundwache.
 */
export const ProjectSupplementQuoteButton: React.FC<Props> = ({ projectId, onOpenQuote, isReadOnly = false }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!projectId || projectId === 'temp-id' || !onOpenQuote || isReadOnly) return null;

    const anlegen = async () => {
        setBusy(true);
        setError(null);
        try {
            const q = await createSupplementQuote({ projectId });
            onOpenQuote(q.id);
        } catch (e) {
            // Klartext des Dienstes (Projekt ohne Kunde, archiviert, Tochter)
            // oder der Rechtefehler der Datenbank.
            setError(rechtFehlerOder(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-inner">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                    <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        <FilePlus className="w-4 h-4 text-blue-600" /> Nachtragsangebot
                    </h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                        Zusätzliche Leistungen anbieten. Bei Annahme erhöhen sie die Mengen dieses Projekts.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={anlegen}
                    disabled={busy}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-300 text-sm font-bold text-slate-700 hover:border-blue-400 hover:text-blue-700 transition-all disabled:opacity-60 shrink-0"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FilePlus className="w-4 h-4" />}
                    {busy ? 'Wird angelegt…' : 'Nachtrag anlegen'}
                </button>
            </div>
            {error && (
                <div className="mt-3 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg p-2">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>Nachtragsangebot konnte nicht angelegt werden: {error}</span>
                </div>
            )}
        </div>
    );
};
